<template>
    <div class="bubble" :class="modifier">
        <p v-if="isDictated" class="bubble__heard">
            <v-icon x-small class="bubble__heard-icon">mdi-microphone</v-icon>
            <span>{{ heardLabel }}</span>
        </p>

        <!-- A dictated turn has nothing to show until the recogniser has been
             through it. Without this the bubble sits empty under its own label,
             which reads as a message that failed rather than one in flight. -->
        <p
            v-if="isPending"
            class="bubble__dots"
            :aria-label="$t('pages.assistant.chat.transcribing')"
        >
            <span class="bubble__dot"></span>
            <span class="bubble__dot"></span>
            <span class="bubble__dot"></span>
        </p>

        <template v-else>
            <!-- Records the assistant named come back marked, and the marks
                 turn into links here. A reply that names none — and every turn
                 of ours — falls through to plain text. -->
            <AssistantRichText
                v-if="hasLead"
                :text="lead.text"
                :marked="lead.marked"
                :entities="message.entities"
            />

            <div v-if="cards.length" class="bubble__cards">
                <AssistantWigCard
                    v-for="card in cards"
                    :key="card.wig.id"
                    :wig="card.wig"
                    :client="card.client"
                />
            </div>

            <!-- The block carries the count it was cut from, and a list showing
                 three of thirteen has to say so. -->
            <p v-if="totals" class="bubble__hidden">
                {{ $t('pages.assistant.card.more', totals) }}
            </p>
        </template>
    </div>
</template>

<script>
import AssistantRichText from './assistant-rich-text.vue'
import AssistantWigCard from './assistant-wig-card.vue'
import { dropCardedLines, readCardRefs, readListTotals } from '../entities'

export default {
    name: 'AssistantMessageBubble',
    components: { AssistantRichText, AssistantWigCard },
    props: {
        message: {
            type: Object,
            required: true,
        },
        // Labels the transcript, so a misrecognition is visible as one rather
        // than read as the assistant misunderstanding.
        isDictated: {
            type: Boolean,
            default: false,
        },
        // The recording is on its way and the words do not exist yet.
        isPending: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        modifier: function () {
            return [
                `bubble--${this.message.role}`,
                // Cards need the width; a sentence looks orphaned with it.
                this.cards.length ? 'bubble--wide' : '',
            ]
        },
        /** One card per wig the reply asked to be listed, client resolved. */
        cards: function () {
            const entities = this.message.entities

            if (!entities) return []

            return readCardRefs(this.message.blocks)
                .map((ref) => entities[ref])
                .filter(Boolean)
                .map((wig) => ({
                    wig,
                    client: wig.client?.ref
                        ? entities[wig.client.ref] || null
                        : null,
                }))
        },
        totals: function () {
            return this.cards.length
                ? readListTotals(this.message.blocks)
                : null
        },
        /**
         * The words above the cards. With cards there is no falling back to the
         * plain text: that is the whole list in prose, which the cards just
         * replaced.
         */
        lead: function () {
            if (!this.cards.length) {
                return {
                    text: this.message.content,
                    marked: this.message.marked,
                }
            }

            const refs = readCardRefs(this.message.blocks)
            const marked = dropCardedLines(this.message.marked, refs)

            return marked
                ? { text: '', marked }
                : { text: this.message.marked ? '' : this.message.content }
        },
        hasLead: function () {
            return !!(this.lead.text || this.lead.marked)
        },
        heardLabel: function () {
            return this.$t('pages.assistant.chat.heard')
        },
    },
}
</script>

<style scoped lang="scss">
.bubble {
    max-width: 85%;
    // A list of cards is the content, not a remark about it.
    &--wide {
        max-width: 100%;
        width: 100%;
    }

    padding: 8px 12px;
    border-radius: 12px;
    margin-bottom: 8px;
    word-break: break-word;
    white-space: pre-wrap;

    // The assistant speaks in the colour of the feature and the manager answers
    // in the neutral one: the eye lands on what came back, which is the half
    // that has to be read.
    &--user {
        margin-left: auto;
        background: #eceeef;
        border-bottom-right-radius: 2px;

        @include for-hebrew {
            margin-left: 0;
            margin-right: auto;
            border-bottom-right-radius: 12px;
            border-bottom-left-radius: 2px;
        }
    }

    &--assistant {
        margin-right: auto;
        background: #967adc;
        color: $color-primary-light;
        border-bottom-left-radius: 2px;

        @include for-hebrew {
            margin-right: 0;
            margin-left: auto;
            border-bottom-left-radius: 12px;
            border-bottom-right-radius: 2px;
        }
    }

    // An eyebrow over the transcript: it says the words below were heard, not
    // typed, and the rule under it separates the two without a second colour.
    &__heard {
        display: flex;
        align-items: center;
        gap: 5px;
        margin: 0 0 6px;
        padding-bottom: 5px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.12);
        font-size: 11px;
        line-height: 1;
        letter-spacing: 0.07em;
        text-transform: uppercase;
        opacity: 0.85;
    }

    &__heard-icon {
        color: inherit !important;
    }

    &__cards {
        // Only when something was said above them.
        margin-top: 8px;

        &:first-child {
            margin-top: 0;
        }
    }

    &__hidden {
        margin: 8px 0 0;
        font-size: 12px;
        opacity: 0.8;
        // Not part of the reply's own line breaks — see the card.
        white-space: normal;
    }

    &__dots {
        display: flex;
        align-items: center;
        gap: 5px;
        margin: 0;
        height: 20px;
    }

    &__dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: currentColor;
        animation: bubble-dot 1300ms ease-in-out infinite;

        &:nth-child(2) {
            animation-delay: 170ms;
        }

        &:nth-child(3) {
            animation-delay: 340ms;
        }

        @media (prefers-reduced-motion: reduce) {
            animation: none;
            opacity: 0.6;
        }
    }
}

@keyframes bubble-dot {
    0%,
    100% {
        opacity: 0.35;
        transform: translateY(0);
    }

    50% {
        opacity: 1;
        transform: translateY(-3px);
    }
}
</style>
